import React, { useState, useRef, useEffect, useMemo } from "react";
import { DatePicker, Input, Select, Spin, message, Modal } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { CALL_FEATCH_API } from "common/API";
import { ButtonOutlined, ButtonFilled } from "reusableComponent/UIButtons";
import {
	useLocation,
	useHistory,
} from "react-router-dom/cjs/react-router-dom.min";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import dayjs from "dayjs";

const { Option } = Select;

/* ── Sentinel value for the section divider ── */
const DIVIDER_VALUE = "__DIVIDER__";

function formatDate(dateStr) {
	if (!dateStr) return "–";
	const date = new Date(dateStr);
	if (isNaN(date)) return dateStr;
	const day = date.getDate();
	const suffix =
		day % 10 === 1 && day !== 11
			? "st"
			: day % 10 === 2 && day !== 12
				? "nd"
				: day % 10 === 3 && day !== 13
					? "rd"
					: "th";
	const month = date.toLocaleDateString("en-GB", { month: "short" });
	return `${day}${suffix} ${month} ${date.getFullYear()}`;
}

function getCurrencySymbol(currency) {
	const map = {
		INR: "₹", EUR: "€", GBP: "£", AUD: "A$", JPY: "¥",
		USD: "$", CAD: "CA$", KES: "KSh", NGN: "₦", GHS: "₵",
		ZAR: "R", EGP: "E£", UGX: "USh", TZS: "TSh", RWF: "RF",
		MAD: "MAD", XOF: "CFA", XAF: "FCFA", ETB: "Br", MUR: "Rs",
		SGD: "S$", HKD: "HK$", NZD: "NZ$", CHF: "CHF", SEK: "kr",
		NOK: "kr", DKK: "kr", MXN: "MX$", BRL: "R$", AED: "د.إ",
		SAR: "﷼", QAR: "﷼", KWD: "د.ك", BHD: ".د.ب", OMR: "﷼",
		CNY: "¥", KRW: "₩", THB: "฿", MYR: "RM", IDR: "Rp",
		PHP: "₱", VND: "₫", PKR: "₨", BDT: "৳", LKR: "Rs",
	};
	if (!currency) return "";
	return map[currency] ?? currency;
}

/* ═══════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════ */
const PageWrapper = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #f3f4f6;
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const PageBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  font-size: 14px;
  color: #6b7280;
  span.link {
    cursor: pointer;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    &:hover { text-decoration: underline; }
  }
  span.active {
    color: #111827;
    font-weight: 600;
  }
`;

const PageTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 28px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 32px;
  margin-bottom: 28px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  span.req {
    color: #ef4444;
    margin-left: 2px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 32px;
  align-items: flex-start;
`;

/* ── Payment Table ── */
const PaymentTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 32px;
`;

const PaymentTableHeader = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  background: #f9fafb;
  border-bottom: 1.5px solid #e5e7eb;
  align-items: stretch;
`;

const PaymentTableRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  align-items: start;
`;

const HeaderLeft = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr;
  padding: 12px 20px;
  align-items: center;
  gap: 0;
`;

const HeaderRight = styled.div`
  padding: 12px 20px;
  border-left: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
`;

const RowLeft = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr;
  padding: 14px 20px;
  align-items: start;
  gap: 0;
`;

const RowRight = styled.div`
  padding: 14px 20px;
  border-left: 1px solid #e5e7eb;
`;

const PaymentTableHeaderCell = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const AmountPaidInputWrap = styled.div`
  display: flex;
  align-items: center;
  border: 1.5px solid
    ${({ $error, $focused }) =>
		$error ? "#ef4444" : $focused ? "#0d9488" : "#e5e7eb"};
  border-radius: 8px;
  overflow: hidden;
  height: 42px;
  max-width: 260px;
  transition: border-color 0.15s;
`;

const CurrencyPrefix = styled.span`
  padding: 0 12px;
  background: #f9fafb;
  border-right: 1.5px solid
    ${({ $error, $focused }) =>
		$error ? "#ef4444" : $focused ? "#0d9488" : "#e5e7eb"};
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  height: 100%;
  display: flex;
  align-items: center;
  transition: border-color 0.15s;
  white-space: nowrap;
`;

const AmountInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  color: #111827;
  padding: 0 12px;
  flex: 1;
  height: 100%;
  &::placeholder { color: #9ca3af; }
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

const PaidLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-underline-offset: 2px;
  &:hover { color: #1d4ed8; }
`;

/* ── Payment History Modal Styles ── */
const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const HistoryTh = styled.th`
  text-align: left;
  padding: 10px 14px;
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  border-bottom: 1.5px solid #e5e7eb;
`;

const HistoryTd = styled.td`
  padding: 12px 14px;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
`;

/* ═══════════════════════════════════════════════════════
   OPTION ROW
═══════════════════════════════════════════════════════ */
function OptionRow({ opt }) {
	/* Section divider — not selectable */
	if (opt.value === DIVIDER_VALUE) {
		return (
			<div style={{
				height: 1,
				background: "#e5e7eb",
				margin: "4px 0",
				pointerEvents: "none",
				userSelect: "none",
			}} />
		);
	}

	const accountName =
		opt.value === "DIRECT_PAYMENT"
			? "Direct Payment"
			: opt.accountname || opt.mattername || opt.label || "–";

	const typeLabel =
		opt.value === "DIRECT_PAYMENT"
			? ""
			: opt.accounttype?.toLowerCase() === "firm account"
				? "Firm Account"
				: opt.isUnallocated                          // ← check flag first
					? "Unallocated Fund"
					: opt.mattername && opt.accountname
						? "Allocated Matter Fund"
						: opt.accountname
							? "Client Account"
							: opt.mattername
								? opt.mattername
								: "";

	const currencyBadge =
		opt.currency && opt.amount !== undefined && opt.amount !== ""
			? `${getCurrencySymbol(opt.currency)}${opt.amount}`
			: opt.currency
				? opt.currency
				: "";

	return (
		<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
			<div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
				<span style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>
					{accountName}
					{typeLabel && (
						<span style={{ fontSize: 14, color: "#6b7280" }}>  ({typeLabel})</span>
					)}
				</span>
			</div>
			{currencyBadge && (
				<span style={{ fontSize: 14, color: "#6b7280", whiteSpace: "nowrap" }}>({currencyBadge})</span>
			)}
		</div>
	);
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
export default function MarkAsPaidPage({ record: recordProp, onBack, onConfirm, page: pageprop }) {
	const userInfo = useSelector((store) => get(store, "common.userInfo"));
	const userEmail = userInfo?.email || userInfo?.userid || "";
	const location = useLocation();
	const page = pageprop || location.state?.page || "fundrequest";
	const record = recordProp || location.state?.record;
	const history = useHistory();
	const matterData = location.state?.matterData || {};
	const amountforBilling = location.state?.amount || {};
	const billingData = location?.state?.billingData || [];
	const [source, setSource] = useState(undefined);
	const [destination, setDestination] = useState(undefined);
	const [payer, setPayer] = useState("");
	const [otherRemarks, setOtherRemarks] = useState("");
	const [amountPaid, setAmountPaid] = useState(
		location.state?.billingTabReturn
			? (amountforBilling?.amount || "")
			: (amountforBilling?.amountpaid || record?.amountpaid || "")
	);

	const [amountFocused, setAmountFocused] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const [paymentDate, setPaymentDate] = useState(dayjs());

	const [sourceOptions, setSourceOptions] = useState([]);
	const [destinationOptions, setDestinationOptions] = useState([]);
	const [loadingOptions, setLoadingOptions] = useState(false);

	/* ── Payment History Modal state ── */
	const [historyModalOpen, setHistoryModalOpen] = useState(false);
	const [historyData, setHistoryData] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);

	const containerRef = useRef(null);

	const isFromBillingModule =
		!!location.state?.matterData && !location.state?.record;
	const typeofmovement = isFromBillingModule
		? "Fund Request Matter"
		: "Fund Request Account";

	const clientName = useMemo(() => {
		return (
			record?.clientname ||
			record?.client ||
			matterData?.clientname ||
			matterData?.clientName ||
			matterData?.client?.clientName ||
			(typeof matterData?.client === "string" ? matterData.client : "") ||
			""
		);
	}, [record, matterData]);

	const toOption = (item) => {
		const amt = item.amount ?? item.balance ?? "";
		return {
			value: item.accountid ?? item.matterid ?? item.id,
			label: [item.mattername, item.accountname ?? item.name].filter(Boolean).join(" - "),
			mattername: item.mattername ?? "",
			matterid: item.matterid ?? "",
			accountname: item.accountname ?? item.name ?? "",
			currency: item.currency ?? "",
			amount: amt,
			balance: parseFloat(amt || 0),
			accounttype: item.accounttype || "",
		};
	};

	const requestCurrency = useMemo(
		() => record?.currency || matterData?.currency || "",
		[record?.currency, matterData?.currency]
	);

	useEffect(() => {
		const clientId =
			record?.clientid ||
			record?.clientId ||
			matterData?.clientId ||
			matterData?.clientid ||
			matterData?.client?.clientId ||
			"";
		if (!clientId) return;

		const fetchOptions = async () => {
			setLoadingOptions(true);
			try {
				const clientId =
					record?.clientid || record?.clientId ||
					matterData?.clientId || matterData?.clientid ||
					matterData?.client?.clientId || "";

				const matterId =
					record?.matterid || record?.matterId ||
					matterData?.matterid || matterData?.matterId || "";

				const [
					clientMatterAccountsRes,
					getAccountsRes,
					clientAccountsRes,
					fundRequestMatterRes,
					clientMatterListRes,
				] = await Promise.all([
					CALL_FEATCH_API(
						`insyts-settings/clientaccountsmatters?clientid=${clientId}&typeofmovement=${encodeURIComponent(typeofmovement)}`,
						"GET"
					),
					CALL_FEATCH_API("insyts-settings/getaccounts", "GET"),
					CALL_FEATCH_API(
						`insyts-settings/clientaccountsmatters?clientid=${clientId}&typeofmovement=${encodeURIComponent("Between Accounts")}`,
						"GET"
					),
					matterId
						? CALL_FEATCH_API(
							`insyts-settings/clientaccountsmatters?clientid=${clientId}&typeofmovement=${encodeURIComponent("Fund Request Matter")}`,
							"GET"
						)
						: Promise.resolve(null),
					clientId && matterId
						? CALL_FEATCH_API(
							`insyts-legal/getclientmatterlist?clientid=${clientId}&matterid=${matterId}`,
							"GET"
						)
						: Promise.resolve(null),
				]);

				const responseData =
					clientMatterAccountsRes?.response?.data ??
					clientMatterAccountsRes?.data ?? {};

				const getAccountsData =
					getAccountsRes?.response?.data ??
					getAccountsRes?.response ??
					getAccountsRes ?? [];

				const clientAccountData =
					clientAccountsRes?.response?.data?.accounts ??
					clientAccountsRes?.response?.data ??
					clientAccountsRes?.response ??
					[];

				const isClientAccount = (item) =>
					item.accounttype?.toLowerCase() === "client account";

				const isFirmAccount = (item) =>
					item.accounttype?.toLowerCase() === "firm account";

				const matterFundAmountMap = new Map();
				const matterListRows =
					clientMatterListRes?.response?.data ??
					clientMatterListRes?.data ?? [];
				if (Array.isArray(matterListRows)) {
					matterListRows.forEach((row) => {
						if (row.accountid != null) {
							matterFundAmountMap.set(String(row.accountid), {
								amount: row.amount,
								currency: row.currency,
							});
						}
					});
				}

				const toOptionWithMatterAmount = (item) => {
					const base = toOption(item);
					const key = String(item.accountid ?? item.id ?? "");
					const matterFundData = matterFundAmountMap.get(key);
					if (matterFundData) {
						return {
							...base,
							amount: matterFundData.amount,
							balance: parseFloat(matterFundData.amount || 0),
							currency: matterFundData.currency || base.currency,
						};
					}
					if (matterFundAmountMap.size > 0) {
						return { ...base, amount: "0", balance: 0 };
					}
					return base;
				};

				const accountsList = Array.isArray(getAccountsData) ? getAccountsData : [];
				const clientAccountList = Array.isArray(clientAccountData) ? clientAccountData : [];

				const billingCurrency = record?.currency || matterData?.currency || "";

				const filterByCurrency = (opts) =>
					opts.filter(
						(opt) =>
							opt.value === "DIRECT_PAYMENT" ||
							opt.value === DIVIDER_VALUE ||
							!billingCurrency ||
							!opt.currency ||
							opt.currency === billingCurrency
					);

				const rawSourceItems = [
					...(responseData?.accounts ?? []),
					...(responseData?.matters?.filter(
						(item) => item.matterid === matterData?.matterId
					) ?? []),
				]
					?.filter(isClientAccount)
					?.filter((item) => parseFloat(item.amount ?? item.balance ?? 0) > 0);

				const directPaymentOption = {
					value: "DIRECT_PAYMENT",
					label: "Direct Payment",
					accountname: "Direct Payment",
					currency: record?.currency || "",
				};

				if (isFromBillingModule) {
					/*
					 * ALLOCATED funds — from the primary typeofmovement API response.
					 * These are accounts that already have funds allocated for this client/matter.
					 */
					const allocatedMap = new Map();
					rawSourceItems.forEach((item) => {
						const key = item.accountid ?? item.matterid ?? item.id;
						if (key) allocatedMap.set(String(key), toOption(item));
					});

					/*
					 * UNALLOCATED funds — from the "Between Accounts" API response.
					 * Only include client accounts that match the billing currency
					 * AND are NOT already present in the allocated list.
					 */
					const unallocatedMap = new Map();
					clientAccountList
						.filter(isClientAccount)
						.filter((c) => !billingCurrency || c.currency === billingCurrency)
						.filter((c) => parseFloat(c.amount ?? c.balance ?? 0) > 0)
						.forEach((item) => {
							const key = item.accountid ?? item.id;
							// Remove the allocatedMap exclusion check if allocated accounts
							// should also appear as selectable unallocated sources
							if (key) {
								unallocatedMap.set(String(key), { ...toOption(item), isUnallocated: true });  // ← updated
							}
						});

					const allocatedOpts = filterByCurrency([...allocatedMap.values()]);
					const unallocatedOpts = filterByCurrency([...unallocatedMap.values()]);

					/*
					 * Final order:
					 *   1. Direct Payment
					 *   2. Allocated funds
					 *   3. ── Unallocated Funds ── (divider, only if any exist)
					 *   4. Unallocated funds
					 */
					setSourceOptions([
						directPaymentOption,
						...allocatedOpts,
						...(unallocatedOpts.length
							? [
								{
									value: DIVIDER_VALUE,
									label: "── Unallocated Funds ──",
									disabled: true,
								},
								...unallocatedOpts,
							]
							: []),
					]);
				} else {
					setSourceOptions(filterByCurrency([directPaymentOption, ...rawSourceItems.map(toOption)]));
				}

				const clientAccountsMatterAmountMap = new Map();
				[...(responseData?.accounts ?? []), ...(responseData?.matters ?? [])].forEach((item) => {
					const key = String(item.accountid ?? item.matterid ?? item.id ?? "");
					if (key) {
						clientAccountsMatterAmountMap.set(key, {
							amount: item.amount ?? item.balance ?? "0",
							currency: item.currency ?? "",
						});
					}
				});

				const betweenAccountsAmountMap = new Map();
				clientAccountList.forEach((item) => {
					const key = String(item.accountid ?? item.id ?? "");
					if (key) {
						betweenAccountsAmountMap.set(key, {
							amount: item.amount ?? item.balance ?? "0",
							currency: item.currency ?? "",
						});
					}
				});

				const hasMatterId = !!(
					record?.matterid?.trim() ||
					matterData?.matterid?.trim() ||
					matterData?.matterId?.trim()
				);

				const fundRequestMatterAmountMap = new Map();
				const fundRequestMatterRows =
					fundRequestMatterRes?.response?.data?.matters ??
					fundRequestMatterRes?.response?.data ??
					fundRequestMatterRes?.data ?? [];

				if (Array.isArray(fundRequestMatterRows)) {
					fundRequestMatterRows.forEach((row) => {
						if (row.accountid != null) {
							fundRequestMatterAmountMap.set(String(row.accountid), {
								amount: row.amount,
								currency: row.currency,
							});
						}
					});
				}

				const toOptionForDestination = (item) => {
					const base = toOption(item);
					const key = String(item.accountid ?? item.id ?? "");

					if (hasMatterId) {
						const fundMatterData = fundRequestMatterAmountMap.get(key);
						if (fundMatterData) {
							return {
								...base,
								amount: fundMatterData.amount,
								balance: parseFloat(fundMatterData.amount || 0),
								currency: fundMatterData.currency || base.currency,
							};
						}
						return { ...base, amount: "0", balance: 0 };
					} else {
						const clientMatterData = clientAccountsMatterAmountMap.get(key);
						if (clientMatterData) {
							return {
								...base,
								amount: clientMatterData.amount,
								balance: parseFloat(clientMatterData.amount || 0),
								currency: clientMatterData.currency || base.currency,
							};
						}
						return { ...base, amount: "0", balance: 0 };
					}
				};

				if (isFromBillingModule) {
					const mergedDestMap = new Map();
					accountsList
						.filter(isFirmAccount)
						.filter((c) => c.currency === billingData?.currency)
						.forEach((item) => {
							const key = item.accountid ?? item.id;
							if (key) mergedDestMap.set(key, toOptionForDestination(item));
						});
					[...(responseData?.accounts ?? []), ...(responseData?.matters ?? [])]
						.filter(isFirmAccount)
						.filter((item) => item.currency === billingData?.currency)
						.forEach((item) => {
							const key = item.accountid ?? item.matterid ?? item.id;
							if (key && !mergedDestMap.has(key))
								mergedDestMap.set(key, toOptionForDestination(item));
						});

					setDestinationOptions([...mergedDestMap.values()]);
				} else {
					const allClientItems = [
						...(responseData?.accounts ?? []),
						...(responseData?.matters ?? []),
					].filter(isClientAccount);

					const mergedDestMap = new Map();
					accountsList.filter(isClientAccount).forEach((item) => {
						const key = item.accountid ?? item.id;
						if (key) mergedDestMap.set(key, toOptionForDestination(item));
					});
					allClientItems.forEach((item) => {
						const key = item.accountid ?? item.matterid ?? item.id;
						if (key && !mergedDestMap.has(key))
							mergedDestMap.set(key, toOptionForDestination(item));
					});

					setDestinationOptions([...mergedDestMap.values()]);
				}

			} catch (err) {
				console.error("Error fetching account/matter options:", err);
				message.error("Failed to load source/destination options.");
			} finally {
				setLoadingOptions(false);
			}
		};

		fetchOptions();
	}, [
		record?.clientid,
		record?.clientId,
		matterData?.clientId,
		matterData?.clientid,
		typeofmovement,
	]);

	const selectedSourceOption = useMemo(
		() => sourceOptions.find((opt) => opt.value === source),
		[sourceOptions, source]
	);
	const sourceCurrency = selectedSourceOption?.currency ?? null;
	const sourceBalance = selectedSourceOption?.balance ?? null;

	const filteredDestinationOptions = useMemo(() => {
		const currencyToMatch = sourceCurrency || requestCurrency;

		const hasMatter = !!(
			record?.matterid?.trim() ||
			matterData?.matterid?.trim() ||
			matterData?.matterId?.trim()
		);

		return destinationOptions.filter((opt) => {
			if (!hasMatter && source && opt.value === source) {
				return false;
			}

			if (!currencyToMatch) return true;
			return !opt.currency || opt.currency === currencyToMatch;
		});
	}, [destinationOptions, sourceCurrency, requestCurrency, source, record?.matterid, matterData?.matterid, matterData?.matterId]);

	const handleSourceChange = (val) => {
		setSource(val);
		const newSourceCurrency = sourceOptions.find((o) => o.value === val)?.currency;
		setDestination((prev) => {
			if (!prev) return prev;
			const destOpt = destinationOptions.find((o) => o.value === prev);
			if (!destOpt) return undefined;
			if (destOpt.value === val) return undefined;
			if (newSourceCurrency && destOpt.currency !== newSourceCurrency) return undefined;
			return prev;
		});
		setErrors((prev) => ({ ...prev, source: undefined }));
	};

	const requestedAmount = location.state?.billingTabReturn
		? (billingData?.balance
			? Number(billingData?.balance).toFixed(2)
			: Number(billingData?.amount).toFixed(2))
		: parseFloat(
			record?.balance > 0 ? record?.balance : record?.requestamount || record?.amount || 0
		);

	const amountPaidError = useMemo(() => {
		const val = parseFloat(amountPaid);
		if (!amountPaid || isNaN(val)) return null;
		if (val <= 0) return "Amount must be greater than 0";

		if (requestedAmount > 0 && val > requestedAmount)
			return `Maximum allowable entry is ${getCurrencySymbol(record?.currency)}${requestedAmount}`;

		if (sourceBalance !== null && val > sourceBalance)
			return `Insufficient balance in source account (available: ${getCurrencySymbol(record?.currency)}${sourceBalance})`;

		return null;
	}, [amountPaid, requestedAmount, sourceBalance, record?.currency, location.state?.billingTabReturn]);

	const validate = () => {
		const errs = {};
		if (!source) errs.source = "Source is required";
		if (!destination) errs.destination = "Destination is required";
		if (!payer || !payer.trim()) errs.payer = "Description is required";
		if (!amountPaid || Number(amountPaid) <= 0) errs.amountPaid = "Enter a valid amount";
		if (amountPaidError) errs.amountPaid = amountPaidError;
		return errs;
	};

	/* ── Payment History ── */
	const handleOpenHistory = async () => {
		setHistoryModalOpen(true);
		setHistoryLoading(true);

		try {
			const fundId =
				record?.fundid ||
				record?.id ||
				amountforBilling?.fundId ||
				"";

			const billingId =
				location.state?.billId ||
				amountforBilling?.billId ||
				amountforBilling?._raw?.billingid ||
				"";

			const isFromBillingTab = location.state?.billingTabReturn;

			let endpoint = "";

			if (isFromBillingTab && billingId) {
				endpoint = `insyts/billing/getbillinghistory?billingid=${billingId}`;
			} else if (isFromBillingModule && billingId) {
				endpoint = `insyts/billing/paymenthistory?billingid=${billingId}`;
			} else if (fundId) {
				endpoint = `insyts-legal/getfundrequesthistory?fundid=${fundId}`;
			}

			if (!endpoint) {
				message.error("No valid history source found");
				setHistoryData([]);
				return;
			}

			const res = await CALL_FEATCH_API(endpoint, "GET");

			const rows =
				res?.response?.data ??
				res?.response ??
				res?.data ??
				[];

			setHistoryData(Array.isArray(rows) ? rows : []);
		} catch (err) {
			console.error("Error fetching payment history:", err);
			message.error("Failed to load payment history.");
			setHistoryData([]);
		} finally {
			setHistoryLoading(false);
		}
	};

	const totalPaidSoFar = useMemo(() => {
		if (!historyData.length) return null;
		return historyData.reduce(
			(sum, row) => sum + parseFloat(row.AmountMarkedasPaid || row.amountpaid || row.amount || 0),
			0
		);
	}, [historyData]);

	const paidBadgeLabel = useMemo(() => {
		const paid =
			amountforBilling?._raw?.paidamount ||
			record?.amountpaid ||
			record?.paidamount ||
			matterData?.amountpaid ||
			matterData?.paidamount;

		const parsedPaid = parseFloat(paid);
		if (!paid || isNaN(parsedPaid) || parsedPaid <= 0) return null;

		const sym = getCurrencySymbol(record?.currency || matterData?.currency);
		return `Paid (${sym}${parsedPaid})`;
	}, [record, matterData, amountforBilling]);

	const handleConfirm = async () => {
		const errs = validate();
		if (Object.keys(errs).length > 0) { setErrors(errs); return; }
		setErrors({});
		setSubmitting(true);

		try {
			const selectedSource = sourceOptions.find((opt) => opt.value === source);
			const selectedDestination = destinationOptions.find((opt) => opt.value === destination);
			const isDirectPayment = source === "DIRECT_PAYMENT";
			const rawAmount = String(amountforBilling?._raw?.amount || "0");
			const cleanAmount = parseFloat(rawAmount.replace(/[^0-9.]/g, "")) || 0;

			if (isFromBillingModule) {
				const isAllocatedMatterFund = !isDirectPayment && !!selectedSource?.matterid;
				const payload = {
					markaspaid: true,
					amount: String(cleanAmount),
					amountpaid: String(amountPaid),
					paymentdate: paymentDate
						? paymentDate.format("YYYY-MM-DD")
						: new Date().toISOString().split("T")[0],
					paymentmethod: isDirectPayment ? "Direct Payment" : "",
					remarks: otherRemarks || "Full payment received",
					paiddescription: payer || "",
					fundid: record?.fundid || record?.id || "",
					requestamount: record?.requestamount || record?.amount || 0,
					updatedby: userEmail,
					approvedby: record?.approvedby || "",
					approveddate: record?.approvedat || "",
					createdby: record?.createdby || "",
					createddate: record?.createdat || record?.createddate || "",
					billingid: amountforBilling?.billId,
					destination: selectedDestination?.accountname || selectedDestination?.label || "Company Account",
					destinationaccountid: destination || null,
					source: isDirectPayment ? "Direct Payment" : selectedSource?.accountname || selectedSource?.label || "",
					sourceaccountid: isDirectPayment ? null : source,
					clientid: matterData?.clientId,
					clientname: matterData?.clientName || matterData?.client?.clientName || "",
					sourcematterid: isAllocatedMatterFund ? matterData?.matterId : null,
					sourcemattername: isAllocatedMatterFund ? selectedSource?.mattername || matterData?.jobTitle : "",
					destinationmatterid: matterData?.matterId || null,
					destinationmattername: selectedSource?.mattername || matterData?.jobTitle || "",
					currency: isDirectPayment ? "" : selectedSource?.currency || record?.currency || "USD",
					description: record?.description || "",
					duedate: record?.duedate || "",
					requesttype: record?.requesttype || "",
				};
				console.log("payload", payload);
				const response = await CALL_FEATCH_API("insyts/billing", "patch", payload);
				const success =
					response?.status === "SUCCESS" ||
					response?.status === 200 ||
					response?.code === "SUCCESS" ||
					response?.success === true;
				if (success) {
					message.success(response?.message || "Payment marked as paid successfully!");
					history.push({
						pathname: history.location.pathname.replace('/markaspaid', ''),
						state: { activeTab: 'billing' },
					});
				} else {
					message.error(response?.response?.message || "Failed to mark as paid.");
				}
			} else {
				const payload = {
					fundid: record?.fundid || record?.id || "",
					clientname: record?.clientname || record?.client || "",
					requestamount: record?.requestamount || record?.amount || 0,
					amountpaid: Number(amountPaid || amountforBilling?.amount) || 0,
					paymentdate: paymentDate
						? paymentDate.format("YYYY-MM-DD")
						: new Date().toISOString().split("T")[0],
					paiddescription: payer || "",
					sourceaccountid: isDirectPayment ? null : source,
					destinationaccountid: destination || null,
					source: isDirectPayment ? "Direct Payment" : selectedSource?.accountname || selectedSource?.label || "",
					destination: selectedDestination?.accountname || selectedDestination?.label || "",
					matterid: record?.matterid?.trim() ? record.matterid : null,
					mattername: record?.matter,
					currency: selectedSource?.currency || record?.currency,
					paymentmethod: isDirectPayment ? "Direct Payment" : "",
					remarks: otherRemarks || "Payment completed",
					markaspaid: true,
					updatedby: userEmail,
					clientid: record?.clientid || "",
					approvedby: record?.approvedby || "",
					approveddate: record?.approvedat || "",
					createdby: record?.createdby,
					createddate: record?.createdat || "",
					description: record?.description || "",
					duedate: record?.duedate || "",
					requesttype: record?.requesttype || "",
				};
				const response = await CALL_FEATCH_API("insyts-legal/updatefundrequest", "PATCH", payload);
				const success =
					response?.status === "SUCCESS" ||
					response?.status === 200 ||
					response?.code === "SUCCESS";
				if (success) {
					message.success("Payment marked as paid successfully!");
					if (page === "client" && record?.clientid) {
						history.push(`/client-detail/${record?.clientid}`, { activeTab: "fund" });
					} else if (onConfirm) {
						onConfirm();
					} else {
						history.push("/myleavereport", { activeTab: "fund_approvals" });
					}
				} else {
					message.error(response?.message || "Failed to mark as paid.");
				}
			}
		} catch (err) {
			console.error("MarkAsPaid error:", err);
			message.error("Something went wrong.");
		} finally {
			setSubmitting(false);
		}
	};

	const canSubmit =
		source && destination && payer?.trim() && amountPaid && Number(amountPaid) > 0 && !amountPaidError;

	const currencySymbol = getCurrencySymbol(
		record?.currency ||
		matterData?.currency ||
		matterData?.financial_resourcePlan?.currency
	);

	const displayCurrency =
		record?.currency ||
		matterData?.currency ||
		matterData?.financial_resourcePlan?.currency;

	const displayCurrencySymbol = getCurrencySymbol(displayCurrency);

	const goBack = () => {
		if (location.state?.billingTabReturn) {
			history.push({
				pathname: history.location.pathname.replace('/markaspaid', ''),
				state: { activeTab: 'billing' },
			});
		} else if (onBack) {
			onBack();
		} else {
			history.goBack();
		}
	};

	return (
		<PageWrapper ref={containerRef}>
			{/* Breadcrumb */}
			<PageBreadcrumb>
				<span className="link" onClick={goBack}>Back</span>
				<span><NavigateNextIcon fontSize="small" /></span>
				<span className="active">Marking as Paid</span>
			</PageBreadcrumb>

			<PageTitle>Marking the Payment</PageTitle>

			{/* Row 1: Client + Payment Date */}
			<FormGrid>
				<FormField>
					<FormLabel>Client</FormLabel>
					<Input
						disabled
						value={clientName}
						style={{
							background: "#f3f4f6",
							color: "#374151",
							cursor: "not-allowed",
							WebkitTextFillColor: "#374151",
						}}
					/>
				</FormField>
				<FormField>
					<FormLabel>Payment Date</FormLabel>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
						value={paymentDate}
						onChange={(dayjsObj) => setPaymentDate(dayjsObj)}
					/>
				</FormField>
			</FormGrid>

			{/* Row 2: Source + Destination */}
			<FormRow style={{ marginBottom: 20 }}>
				<FormField>
					<FormLabel>Source <span className="req">*</span></FormLabel>
					<Select
						placeholder={loadingOptions ? "Loading..." : "Select source"}
						value={source}
						onChange={handleSourceChange}
						loading={loadingOptions}
						allowClear
						showSearch
						optionFilterProp="label"
						getPopupContainer={() => containerRef.current || document.body}
					>
						{sourceOptions.map((opt) => (
							<Option
								key={opt.value}
								value={opt.value}
								label={opt.label}
								disabled={opt.disabled || opt.value === DIVIDER_VALUE}
								style={
									opt.value === DIVIDER_VALUE
										? { cursor: "default", padding: "0 12px", minHeight: "auto" }
										: undefined
								}
							>
								<OptionRow opt={opt} />
							</Option>
						))}
					</Select>
					{errors.source && <ErrorText>{errors.source}</ErrorText>}
				</FormField>

				<FormField>
					<FormLabel>Destination <span className="req">*</span></FormLabel>
					<Select
						placeholder={
							loadingOptions
								? "Loading..."
								: sourceCurrency
									? `Select ${sourceCurrency} account`
									: "Select destination"
						}
						value={destination}
						onChange={(val) => {
							setDestination(val);
							setErrors((prev) => ({ ...prev, destination: undefined }));
						}}
						loading={loadingOptions}
						allowClear
						showSearch
						optionFilterProp="label"
						getPopupContainer={() => containerRef.current || document.body}
					>
						{filteredDestinationOptions.map((opt) => (
							<Option key={opt.value} value={opt.value} label={opt.label}>
								<OptionRow opt={opt} />
							</Option>
						))}
					</Select>
					{errors.destination && <ErrorText>{errors.destination}</ErrorText>}
				</FormField>
			</FormRow>

			{/* Row 3: Description + Other Remarks */}
			<FormRow style={{ marginBottom: 28 }}>
				<FormField>
					<FormLabel>Description <span className="req">*</span></FormLabel>
					<Input
						type="text"
						placeholder="Enter description"
						value={payer}
						onChange={(e) => {
							setPayer(e.target.value);
							setErrors((prev) => ({ ...prev, payer: undefined }));
						}}
					/>
					{errors.payer && <ErrorText>{errors.payer}</ErrorText>}
				</FormField>
				<FormField>
					<FormLabel>Other Remarks</FormLabel>
					<Input
						type="text"
						placeholder="Enter remarks (optional)"
						value={otherRemarks}
						onChange={(e) => setOtherRemarks(e.target.value)}
					/>
				</FormField>
			</FormRow>

			{/* ── Payment detail table ── */}
			<PaymentTable>
				<PaymentTableHeader>
					<HeaderLeft>
						<PaymentTableHeaderCell>{record?.requesttype === "Matter Fund" ? "Matter" : "Client"}</PaymentTableHeaderCell>
						<PaymentTableHeaderCell>Created on</PaymentTableHeaderCell>
						<PaymentTableHeaderCell>Amount Requested</PaymentTableHeaderCell>
					</HeaderLeft>
					<HeaderRight>
						<PaymentTableHeaderCell>Amount Paid</PaymentTableHeaderCell>
					</HeaderRight>
				</PaymentTableHeader>

				<PaymentTableRow>
					<RowLeft>
						{/* Matter name + ID */}
						{record?.requesttype === "Matter Fund" ? (
							<div>
								<span style={{ fontWeight: 500, fontSize: 14, color: "#111827", display: "block" }}>
									{record?.mattername ||
										record?.matter ||
										matterData?.mattername ||
										matterData?.jobTitle ||
										matterData?.information?.jobTitle ||
										"–"}
								</span>
								<span style={{ fontSize: 12, color: "#9ca3af" }}>
									{record?.matterid ||
										record?.matterId ||
										matterData?.matterid ||
										matterData?.matterId ||
										""}
								</span>
							</div>
						) : (
							<div>
								<span style={{ fontWeight: 500, fontSize: 14, color: "#111827", display: "block" }}>
									{record?.client || billingData?.clientname || "–"}
								</span>
								<span style={{ fontSize: 12, color: "#9ca3af" }}>
									{record?.clientid || billingData?.clientcode || ""}
								</span>
							</div>
						)}

						{/* Created on */}
						<span style={{ fontSize: 14, color: "#374151" }}>
							{formatDate(
								record?.createddate ||
								record?.createdon ||
								record?.createdat ||
								matterData?.createddate ||
								matterData?.createdon ||
								matterData?.createdat ||
								billingData?.createdAt
							)}
						</span>

						{/* Amount Requested + Paid link */}
						<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
							<span style={{ fontSize: 14, color: "#374151" }}>
								{location.state?.billingTabReturn
									? (billingData?.balance
										? Number(billingData?.balance).toFixed(2)
										: Number(billingData?.amount).toFixed(2) || "–")
									: (record?.balance > 0
										? record?.balance
										: record?.requestamount ||
										record?.amount ||
										matterData?.requestamount ||
										matterData?.amount ||
										matterData?.financial_resourcePlan?.billingAmount ||
										amountforBilling?.amount ||
										"–")
								}
							</span>
							{paidBadgeLabel && (
								<PaidLink onClick={handleOpenHistory}>
									{paidBadgeLabel}
								</PaidLink>
							)}
						</div>
					</RowLeft>

					{/* Amount Paid input */}
					<RowRight>
						<AmountPaidInputWrap $focused={amountFocused} $error={!!amountPaidError}>
							<CurrencyPrefix $focused={amountFocused} $error={!!amountPaidError}>
								{currencySymbol} {displayCurrency}
							</CurrencyPrefix>
							<AmountInput
								type="number"
								placeholder="0"
								value={amountPaid}
								onChange={(e) => {
									setAmountPaid(e.target.value);
									setErrors((prev) => ({ ...prev, amountPaid: undefined }));
								}}
								onFocus={() => setAmountFocused(true)}
								onBlur={() => setAmountFocused(false)}
							/>
						</AmountPaidInputWrap>
						{(amountPaidError || errors.amountPaid) && (
							<ErrorText>{amountPaidError || errors.amountPaid}</ErrorText>
						)}
					</RowRight>
				</PaymentTableRow>
			</PaymentTable>

			{/* Footer */}
			<PageFooter>
				<ButtonOutlined onClick={goBack} disabled={submitting}>Cancel</ButtonOutlined>
				<ButtonFilled onClick={handleConfirm} disabled={!canSubmit || submitting}>
					{submitting ? (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
							<Spin size="small" style={{ filter: "brightness(10)" }} />
							Saving...
						</span>
					) : (
						"Mark as Paid"
					)}
				</ButtonFilled>
			</PageFooter>

			{/* ── Payment History Modal ── */}
			<Modal
				title={null}
				open={historyModalOpen}
				onCancel={() => setHistoryModalOpen(false)}
				footer={null}
				width={760}
				centered
				destroyOnClose
			>
				<div style={{ padding: "4px 0 8px" }}>
					<div style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: 16,
						paddingRight: 32,
					}}>
						<span style={{ fontSize: 14, fontWeight: 700, color: "#111827", flexShrink: 0 }}>
							Payment History
						</span>
						<span style={{ fontSize: 14, color: "#374151", marginLeft: 16 }}>
							Amount requested initially:{" "}
							<span style={{ color: "#0d9488", fontWeight: 700 }}>
								{displayCurrencySymbol}
								{amountforBilling?._raw?.amount || record?.requestamount ||
									record?.amount ||
									matterData?.requestamount ||
									matterData?.amount ||
									"–"}
							</span>
						</span>
					</div>

					{historyLoading ? (
						<div style={{ textAlign: "center", padding: "40px 0" }}>
							<Spin size="default" />
						</div>
					) : historyData.length === 0 ? (
						<div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af", fontSize: 14 }}>
							No payment history found.
						</div>
					) : (
						<>
							<HistoryTable>
								<thead>
									<tr>
										<HistoryTh>S.No</HistoryTh>
										<HistoryTh>Amount Marked as Paid</HistoryTh>
										<HistoryTh>Source</HistoryTh>
										<HistoryTh>Destination</HistoryTh>
										<HistoryTh>Marked on</HistoryTh>
										<HistoryTh>Balance</HistoryTh>
									</tr>
								</thead>
								<tbody>
									{historyData.map((row, idx) => (
										<tr key={row.id || row.historyid || idx}>
											<HistoryTd>{idx + 1}</HistoryTd>
											<HistoryTd>
												{getCurrencySymbol(row.currency || displayCurrency)}
												{row.AmountMarkedasPaid ?? row.amountpaid ?? row.amount ?? "–"}
											</HistoryTd>
											<HistoryTd>{row.Source || row.source || row.sourceaccountname || "–"}</HistoryTd>
											<HistoryTd>{row.Destination || row.destination || row.destinationaccountname || "–"}</HistoryTd>
											<HistoryTd>
												<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
													<CalendarOutlined style={{ color: "#9ca3af" }} />
													{formatDate(row.MarkedOn || row.paymentdate || row.markedon || row.createdat)}
												</span>
											</HistoryTd>
											<HistoryTd>
												{row.Balance !== undefined && row.Balance !== null
													? `${getCurrencySymbol(row.currency || displayCurrency)}${row.Balance}`
													: row.balance !== undefined && row.balance !== null
														? `${getCurrencySymbol(row.currency || displayCurrency)}${row.balance}`
														: "–"}
											</HistoryTd>
										</tr>
									))}
								</tbody>
							</HistoryTable>

							{totalPaidSoFar !== null && (
								<div style={{
									display: "flex",
									justifyContent: "flex-end",
									marginTop: 12,
									paddingTop: 10,
									borderTop: "1px solid #f3f4f6",
									fontSize: 14,
									color: "#374151",
								}}>
									Total paid so far:{" "}
									<span style={{ color: "#0d9488", fontWeight: 700, marginLeft: 6 }}>
										{displayCurrencySymbol}{totalPaidSoFar}
									</span>
								</div>
							)}
						</>
					)}
				</div>
			</Modal>
		</PageWrapper>
	);
}